import { useState, useEffect } from "react";
import { fmtDate, fmtDateTime } from "@/lib/dates";
import { SwipeRow } from "@/components/SwipeRow";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Shield, Users, Flag, LifeBuoy, Megaphone, ScrollText, ArrowLeft,
  Search, Ban, RotateCcw, Trash2, Plus, MailCheck, UserCheck,
} from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Loader } from "@/components/common";
import { Button } from "@/components/Button";
import { Modal, Field, TextInput, TextArea } from "@/components/Modal";

const TABS = [
  { id: "overview", label: "Overview", icon: Shield },
  { id: "users", label: "Users", icon: Users },
  { id: "reports", label: "Reports", icon: Flag },
  { id: "tickets", label: "Support", icon: LifeBuoy },
  { id: "announcements", label: "Announce", icon: Megaphone },
  { id: "audit", label: "Audit", icon: ScrollText },
];

function SupportBadge({ s }) {
  if (!s?.status) return null;
  const map = {
    sent: ["Verify email sent", "bg-amber-500/15 text-amber-400", s.sent_at],
    verified: ["Verified ✓", "bg-emerald-500/15 text-emerald-400", s.verified_at],
    restored: ["Restored", "bg-sky-500/15 text-sky-300", s.restored_at],
  };
  const [label, cls, at] = map[s.status] || [s.status, "bg-slate-800 text-slate-300", null];
  return (
    <span className={`text-xs px-2 py-1 rounded-full ${cls}`} data-testid="admin-support-badge">
      {label}{at ? ` · ${fmtDate(at)}` : ""}
    </span>
  );
}

function Stat({ label, value }) {
  return (
    <div className="iks-card p-4">
      <p className="text-2xl font-display font-bold text-slate-50">{value}</p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

export default function AdminPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [helpReqs, setHelpReqs] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [sending, setSending] = useState(false);
  const [fullDel, setFullDel] = useState(null);

  const confirmFullDelete = async () => {
    setSending(true);
    try {
      await api.delete(`/admin/users/${fullDel.id}/full`);
      toast.success(`${fullDel.email} was permanently deleted`);
      setFullDel(null); await loadTab("users");
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };
  const [announcements, setAnnouncements] = useState([]);
  const [audit, setAudit] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [annModal, setAnnModal] = useState(null);
  const [form, setForm] = useState({});

  const loadTab = async (t) => {
    try {
      if (t === "overview") setStats((await api.get("/admin/stats")).data);
      if (t === "users") setUsers((await api.get("/admin/users", { params: q ? { q } : {} })).data);
      if (t === "reports") setReports((await api.get("/admin/reports")).data);
      if (t === "tickets") {
        setTickets((await api.get("/admin/tickets")).data);
        setHelpReqs((await api.get("/admin/support-requests")).data);
      }
      if (t === "announcements") setAnnouncements((await api.get("/admin/announcements")).data);
      if (t === "audit") setAudit((await api.get("/admin/audit")).data);
    } catch (e) {
      if (e?.response?.status === 403) nav("/profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setLoading(true); loadTab(tab); /* eslint-disable-next-line */ }, [tab]);

  const act = async (fn) => { try { await fn(); await loadTab(tab); } catch (e) { toast.error(apiErr(e)); } };

  const confirmSend = async () => {
    setSending(true);
    try {
      await api.post(`/admin/users/${confirm.user.id}/${confirm.kind === "verify" ? "send-verify" : "account-restored"}`);
      toast.success(confirm.kind === "verify" ? "Verification email sent" : "Account restored email sent");
      setConfirm(null); await loadTab("users");
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  const saveAnn = async () => {
    try {
      if (annModal === "new") await api.post("/admin/announcements", form);
      else await api.put(`/admin/announcements/${form.id}`, form);
      setAnnModal(null); await loadTab("announcements"); toast.success("Saved");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="iks-fade-up">
      <button onClick={() => nav("/profile")} className="flex items-center gap-1.5 text-slate-400 hover:text-slate-200 text-sm mb-4">
        <ArrowLeft size={16} /> Back to app
      </button>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center"><Shield size={20} className="text-amber-400" /></div>
        <h1 className="font-display font-bold text-2xl text-slate-50">Admin Dashboard</h1>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-2 mb-5 -mx-1 px-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? "bg-blue-600 text-white" : "bg-slate-900/60 text-slate-400 hover:text-slate-200"}`}>
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {loading ? <Loader /> : (
        <>
          {tab === "overview" && stats && (
            <div className="space-y-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-sky-400 mb-2">Users</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Stat label="Total users" value={stats.users.total} />
                  <Stat label="Active" value={stats.users.active} />
                  <Stat label="Suspended" value={stats.users.suspended} />
                  <Stat label="Deleted" value={stats.users.deleted} />
                  <Stat label="Unverified" value={stats.users.unverified} />
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-sky-400 mb-2">Connections</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Stat label="Total" value={stats.connections.total} />
                  <Stat label="Pending" value={stats.connections.pending} />
                  <Stat label="Active" value={stats.connections.active} />
                  <Stat label="Archived" value={stats.connections.archived} />
                  <Stat label="Blocked" value={stats.connections.blocked} />
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-sky-400 mb-2">Content</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Stat label="Memories" value={stats.content.memories} />
                  <Stat label="Wishlist items" value={stats.content.wishlist_items} />
                  <Stat label="Savings goals" value={stats.content.savings_goals} />
                  <Stat label="Bills" value={stats.content.bills} />
                  <Stat label="Important dates" value={stats.content.important_dates} />
                  <Stat label="Notes" value={stats.content.notes} />
                </div>
              </div>
            </div>
          )}

          {tab === "users" && (
            <div>
              <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                  <TextInput value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadTab("users")} placeholder="Search name, email, username" className="pl-9" data-testid="admin-user-search" />
                </div>
                <Button onClick={() => loadTab("users")}>Search</Button>
              </div>
              <div className="space-y-2">
                {users.length > 0 && <p className="text-xs text-slate-500 mb-1">Tip: swipe a user left to reveal "Delete Fully".</p>}
                {users.map((u) => (
                  <SwipeRow key={u.id} testId="admin-user-swipe" action={(close) => (
                    <button onClick={() => { close(); setFullDel(u); }} data-testid="admin-delete-fully-btn"
                      className="w-full bg-red-600 hover:bg-red-500 text-white text-xs font-semibold flex flex-col items-center justify-center gap-1">
                      <Trash2 size={18} /> Delete Fully
                    </button>
                  )}>
                  <div className="iks-card p-4" data-testid="admin-user-row">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-100 font-medium break-words">{u.name} <span className="text-slate-500 text-sm">@{u.username}</span></p>
                        <p className="text-slate-500 text-xs break-all">{u.email} · {u.email_verified ? "verified" : "unverified"}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${u.status === "active" ? "bg-emerald-500/15 text-emerald-400" : u.status === "suspended" ? "bg-amber-500/15 text-amber-400" : "bg-red-500/10 text-red-400"}`}>{u.status}</span>
                      {u.status === "active" ? (
                        <button onClick={() => act(() => api.post(`/admin/users/${u.id}/suspend`, { reason: "" }))} className="p-1.5 text-slate-400 hover:text-amber-400 shrink-0" title="Suspend" data-testid="suspend-btn"><Ban size={16} /></button>
                      ) : (
                        <button onClick={() => act(() => api.post(`/admin/users/${u.id}/restore`))} className="p-1.5 text-slate-400 hover:text-emerald-400 shrink-0" title="Restore" data-testid="restore-btn"><RotateCcw size={16} /></button>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <button onClick={() => setConfirm({ kind: "verify", user: u })} data-testid="admin-send-verify-btn"
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-slate-800 text-slate-200 hover:bg-slate-700 transition-colors"><MailCheck size={14} /> Verify Email</button>
                      <button onClick={() => setConfirm({ kind: "restored", user: u })} data-testid="admin-account-restored-btn"
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-slate-800 text-slate-200 hover:bg-slate-700 transition-colors"><UserCheck size={14} /> Account Restored</button>
                      <SupportBadge s={u.support} />
                    </div>
                  </div>
                  </SwipeRow>
                ))}
                {users.length === 0 && <p className="text-slate-500 text-sm iks-card p-4">No users found.</p>}
              </div>
            </div>
          )}

          {tab === "reports" && (
            <div className="space-y-2">
              {reports.map((r) => (
                <div key={r.id} className="iks-card p-4" data-testid="admin-report-row">
                  <div className="flex items-center justify-between">
                    <p className="text-slate-100 text-sm font-medium">{r.reason} <span className="text-slate-500">· {r.target_type}</span></p>
                    <select value={r.status} onChange={(e) => act(() => api.put(`/admin/reports/${r.id}`, { status: e.target.value }))} className="iks-input px-2 py-1 text-xs">
                      {["new", "reviewing", "resolved"].map((s) => <option key={s} value={s} className="bg-slate-900">{s}</option>)}
                    </select>
                  </div>
                  {r.description && <p className="text-slate-400 text-sm mt-1">{r.description}</p>}
                  <p className="text-slate-600 text-xs mt-1">by {r.reporter_name}</p>
                </div>
              ))}
              {reports.length === 0 && <p className="text-slate-500 text-sm iks-card p-4">No reports.</p>}
            </div>
          )}

          {tab === "tickets" && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-sky-400 mb-1">Login help (from login screen)</p>
              {helpReqs.map((r) => (
                <div key={r.id} className="iks-card p-4" data-testid="admin-help-request-row">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-slate-100 text-sm font-medium break-words min-w-0">{r.name} <span className="text-slate-500 font-normal break-all">· {r.email}</span></p>
                    <select value={r.status} onChange={(e) => act(() => api.put(`/admin/support-requests/${r.id}`, { status: e.target.value }))} className="iks-input !w-auto px-2 py-1 text-xs shrink-0">
                      {["new", "in_progress", "resolved"].map((s) => <option key={s} value={s} className="bg-slate-900">{s}</option>)}
                    </select>
                  </div>
                  <p className="text-slate-400 text-sm mt-1 whitespace-pre-line break-words">{r.message}</p>
                  <p className="text-slate-600 text-xs mt-1">{fmtDateTime(r.created_at)}</p>
                </div>
              ))}
              {helpReqs.length === 0 && <p className="text-slate-500 text-sm iks-card p-4">No login help requests.</p>}
              <p className="text-xs font-semibold uppercase tracking-wider text-sky-400 mt-6 mb-1">In-app support tickets</p>
              {tickets.map((t) => (
                <div key={t.id} className="iks-card p-4" data-testid="admin-ticket-row">
                  <div className="flex items-center justify-between">
                    <p className="text-slate-100 text-sm font-medium">{t.subject}</p>
                    <select value={t.status} onChange={(e) => act(() => api.put(`/admin/tickets/${t.id}`, { status: e.target.value }))} className="iks-input px-2 py-1 text-xs">
                      {["new", "in_progress", "resolved"].map((s) => <option key={s} value={s} className="bg-slate-900">{s}</option>)}
                    </select>
                  </div>
                  <p className="text-slate-400 text-sm mt-1">{t.message}</p>
                  <p className="text-slate-600 text-xs mt-1">{t.user_name} · {t.user_email}</p>
                </div>
              ))}
              {tickets.length === 0 && <p className="text-slate-500 text-sm iks-card p-4">No tickets.</p>}
            </div>
          )}

          {tab === "announcements" && (
            <div>
              <div className="flex justify-end mb-4"><Button onClick={() => { setForm({ title: "", body: "", status: "draft" }); setAnnModal("new"); }} data-testid="add-announcement-btn"><Plus size={16} /> New</Button></div>
              <div className="space-y-2">
                {announcements.map((a) => (
                  <div key={a.id} className="iks-card p-4 flex items-start gap-3">
                    <div className="flex-1">
                      <p className="text-slate-100 font-medium">{a.title} <span className={`text-xs ml-1 px-2 py-0.5 rounded-full ${a.status === "published" ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700 text-slate-400"}`}>{a.status}</span></p>
                      <p className="text-slate-400 text-sm mt-1 whitespace-pre-line">{a.body}</p>
                    </div>
                    <button onClick={() => { setForm(a); setAnnModal("edit"); }} className="text-slate-400 hover:text-sky-400 text-xs">Edit</button>
                    <button onClick={() => act(() => api.delete(`/admin/announcements/${a.id}`))} className="text-slate-400 hover:text-red-400"><Trash2 size={15} /></button>
                  </div>
                ))}
                {announcements.length === 0 && <p className="text-slate-500 text-sm iks-card p-4">No announcements.</p>}
              </div>
            </div>
          )}

          {tab === "audit" && (
            <div className="space-y-2">
              {audit.map((a, i) => (
                <div key={i} className="iks-card p-3 flex items-center gap-3 text-sm">
                  <ScrollText size={15} className="text-slate-500" />
                  <span className="text-slate-200">{a.action}</span>
                  {a.detail && <span className="text-slate-500">· {a.detail}</span>}
                  <span className="ml-auto text-slate-600 text-xs">{a.admin_email}</span>
                </div>
              ))}
              {audit.length === 0 && <p className="text-slate-500 text-sm iks-card p-4">No admin actions logged yet.</p>}
            </div>
          )}
        </>
      )}

      <Modal open={!!fullDel} onClose={() => setFullDel(null)} title="Confirm Full Delete" testId="admin-full-delete-modal"
        footer={<><Button variant="secondary" full onClick={() => setFullDel(null)} data-testid="admin-full-delete-cancel">Cancel</Button><Button variant="destructive" full loading={sending} onClick={confirmFullDelete} data-testid="admin-full-delete-confirm">Confirm Full Delete</Button></>}>
        {fullDel && (
          <div className="text-sm text-slate-300 space-y-2">
            <p>Permanently delete <b>{fullDel.name}</b> (<span className="break-all">{fullDel.email}</span>, @{fullDel.username})?</p>
            <p className="text-slate-400">This removes the account, their profile, wishlist, notifications and every shared space they're in (including the other person's copy). Their email and username become free to use again.</p>
            <p className="text-red-400 font-medium">This can't be undone.</p>
          </div>
        )}
      </Modal>

      <Modal open={!!confirm} onClose={() => setConfirm(null)} title="Confirm Send Email" testId="admin-confirm-send-modal"
        footer={<><Button variant="secondary" full onClick={() => setConfirm(null)} data-testid="admin-confirm-cancel">Cancel</Button><Button full loading={sending} onClick={confirmSend} data-testid="admin-confirm-send">Confirm Send Email</Button></>}>
        {confirm && (
          <p className="text-slate-300 text-sm">
            {confirm.kind === "verify"
              ? <>Send a <b>"Please verify your email"</b> email to <b className="break-all">{confirm.user.email}</b> ({confirm.user.name})?</>
              : <>Send an <b>"Your account has been restored"</b> email to <b className="break-all">{confirm.user.email}</b> ({confirm.user.name})? This also unlocks the account (removes any suspension or login lockout) and includes a secure link to set a new password.</>}
          </p>
        )}
      </Modal>

      <Modal open={!!annModal} onClose={() => setAnnModal(null)} title={annModal === "new" ? "New announcement" : "Edit announcement"}
        footer={<><Button variant="secondary" full onClick={() => setAnnModal(null)}>Cancel</Button><Button full onClick={saveAnn}>Save</Button></>}>
        <Field label="Title"><TextInput value={form.title || ""} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></Field>
        <Field label="Body"><TextArea value={form.body || ""} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} /></Field>
        <Field label="Status">
          <select value={form.status || "draft"} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="iks-input w-full px-3.5 py-2.5 text-sm">
            {["draft", "published", "archived"].map((s) => <option key={s} value={s} className="bg-slate-900">{s}</option>)}
          </select>
        </Field>
      </Modal>
    </div>
  );
}
